<script>
import { useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import CannedResponse from './CannedResponse.vue';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AddCanned from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { buildLineEmojiImageUrl } from 'shared/helpers/lineEmoji';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  extractTextFromMarkdown,
  replaceSimplifiedVariables,
} from 'dashboard/helper/editorHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import WlabEmojiPicker from 'shared/components/emoji/WlabEmojiPicker.vue';

const LINE_EMOJI_TOKEN_REGEX = /\[line_emoji:([0-9A-Za-z]+):([0-9A-Za-z]+)\]/g;
const LINE_EMOJI_TOKEN_TEST_REGEX =
  /\[line_emoji:[0-9A-Za-z]+:[0-9A-Za-z]+\]/;

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    CannedResponse,
    ReplyBoxBanner,
    Switch,
    AddCanned,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ResizableTextArea,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    WlabEmojiPicker,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
    forcePrivateNoteMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox'],
  setup() {
    const {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');

    return {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      replyEditor,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      emojiPickerMounted: false,
      emojiPickerWarmupTimer: null,
      emojiPickerWarmupIdleId: null,
      emojiPickerAnchorX: 0,
      emojiPickerAnchorBottom: 0,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      publishTypingPreviewDebounced: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      showTagPicker: false,
      tagSearchQuery: '',
      showCannedPicker: false,
      cannedSearchQuery: '',
      cannedPickerSelectedShortCode: '',
      showCannedPickerSettings: false,
      showAddCannedModal: false,
      cannedPickerAnchorX: 0,
      cannedPickerAnchorY: 0,
      cannedPickerShowAllUngroupedLocal: false,
      cannedPickerKeywordModeLocal: false,
      cannedPickerTriggeredBySlash: false,
      recentlyAppliedSuggestedTag: '',
      stickySuggestedTags: [],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountLabels: 'labels/getLabels',
      getGroupedCannedResponses: 'getGroupedCannedResponses',
      cannedMessages: 'getCannedResponses',
    }),
    cannedPickerShowAllUngrouped() {
      return this.cannedPickerShowAllUngroupedLocal;
    },
    cannedPickerKeywordMode() {
      return this.cannedPickerKeywordModeLocal;
    },
    filteredGroupedCanned() {
      const groups = this.getGroupedCannedResponses('asc');
      const q = (this.cannedSearchQuery || '').toLowerCase().trim();
      if (!q) return groups;
      const filtered = {};
      Object.entries(groups).forEach(([groupName, items]) => {
        const match = items.filter(
          r =>
            r.short_code.toLowerCase().includes(q) ||
            r.content.toLowerCase().includes(q)
        );
        if (match.length) filtered[groupName] = match;
      });
      return filtered;
    },
    filteredCannedRecords() {
      const q = (this.cannedSearchQuery || '').toLowerCase().trim();
      const records = this.cannedMessages || [];
      if (!q) return records;
      return records.filter(
        r =>
          (r.short_code || '').toLowerCase().includes(q) ||
          (r.content || '').toLowerCase().includes(q)
      );
    },
    cannedPickerGroupedView() {
      const records = this.filteredCannedRecords || [];
      if (this.cannedPickerShowAllUngrouped) {
        return { All: records };
      }
      const groups = {};
      records.forEach(record => {
        const groupName = record.group_name || 'General';
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(record);
      });
      const ordered = {};
      if (groups.General) ordered.General = groups.General;
      Object.keys(groups)
        .filter(k => k !== 'General')
        .sort((a, b) => a.localeCompare(b))
        .forEach(k => {
          ordered[k] = groups[k];
        });
      return ordered;
    },
    cannedPickerKeywordsByGroup() {
      if (!this.cannedPickerKeywordMode) return {};
      const grouped = this.cannedPickerGroupedView;
      const result = {};
      Object.entries(grouped).forEach(([groupName, items]) => {
        const byCode = new Map();
        items.forEach(r => {
          const key = (r.short_code || '').trim();
          if (!key) return;
          const prev = byCode.get(key) || { short_code: key, count: 0 };
          byCode.set(key, { short_code: key, count: prev.count + 1 });
        });
        result[groupName] = [...byCode.values()].sort((a, b) =>
          a.short_code.localeCompare(b.short_code)
        );
      });
      return result;
    },
    cannedPickerDetailResponses() {
      if (!this.cannedPickerSelectedShortCode) return [];
      return (this.filteredCannedRecords || []).filter(
        r => (r.short_code || '').trim() === this.cannedPickerSelectedShortCode
      );
    },
    cannedPickerStyle() {
      return {
        left: `${this.cannedPickerAnchorX}px`,
        bottom: `${window.innerHeight - this.cannedPickerAnchorY}px`,
      };
    },
    emojiPickerStyle() {
      return {
        left: `${this.emojiPickerAnchorX}px`,
        bottom: `${this.emojiPickerAnchorBottom}px`,
        position: 'fixed',
        zIndex: 2000,
      };
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel
      );
    },
    showRichContentEditor() {
      // LINE channels must use plain-text editor so the emoji preview overlay works
      if (this.isALineChannel && !this.isOnPrivateNote) {
        return false;
      }

      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    showWhatsappTemplates() {
      return this.isAWhatsAppCloudChannel && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    assignedAgentId() {
      return Number(this.currentChat?.meta?.assignee?.id || 0);
    },
    currentUserId() {
      return Number(this.currentUser?.id || 0);
    },
    forcePrivateNoteByAssignment() {
      return (
        this.forcePrivateNoteMode ||
        (this.assignedAgentId > 0 &&
          this.currentUserId > 0 &&
          this.assignedAgentId !== this.currentUserId)
      );
    },
    isPrivate() {
      if (this.forcePrivateNoteByAssignment) {
        return true;
      }
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        this.forcePrivateNoteByAssignment ||
        (!this.currentChat?.can_reply && !this.isAWhatsAppChannel)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.signatureToApply;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    lineUserId() {
      // 获取 LINE User ID from contact_inbox.source_id
      return this.currentChat?.contact_inbox?.source_id || '';
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });

      // Add original_name from contact's additional_attributes
      // If original_name doesn't exist, fallback to current name
      const originalName =
        this.currentContact?.additional_attributes?.original_name ||
        this.currentContact?.name ||
        '';
      variables['contact.original_name'] = originalName;

      return variables;
    },
    // ensure that the signature is plain text depending on `showRichContentEditor`
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    hasInlineLineEmojiTokens() {
      return LINE_EMOJI_TOKEN_TEST_REGEX.test(String(this.message || ''));
    },
    lineEmojiComposerPreviewHtml() {
      if (!this.hasInlineLineEmojiTokens) return '';
      return this.buildLineEmojiComposerPreviewHtml(this.message);
    },
    hasRenderableLineEmojiComposerPreview() {
      return this.lineEmojiComposerPreviewHtml.includes(
        'wlab-line-emoji-composer__img'
      );
    },
    conversationLabels() {
      if (!this.currentChat?.id) return [];
      return (
        this.$store.getters['conversationLabels/getConversationLabels'](
          this.currentChat.id
        ) || []
      );
    },
    suggestedTagsForCurrentMessage() {
      const plainMessage = extractTextFromMarkdown(
        removeSignature(this.message || '', this.signatureToApply)
      );
      const normalizedMessage = this.normalizeTextForSuggestion(plainMessage);
      if (normalizedMessage.length < 4) return [];

      const matchedTags = new Set();

      (this.cannedMessages || []).forEach(response => {
        const suggestTags = Array.isArray(response?.suggest_tags)
          ? response.suggest_tags
          : [];
        if (!suggestTags.length) return;

        const originalCannedText = response?.content || '';
        const normalizedResponse = this.normalizeTextForSuggestion(originalCannedText);
        const renderedCannedText = replaceVariablesInMessage({
          message: replaceSimplifiedVariables(originalCannedText),
          variables: this.messageVariables,
        });
        const normalizedRenderedResponse =
          this.normalizeTextForSuggestion(renderedCannedText);

        const isSimilarToOriginal =
          normalizedResponse.length >= 4 &&
          this.isSimilarResponseText(normalizedMessage, normalizedResponse);
        const isSimilarToRendered =
          normalizedRenderedResponse.length >= 4 &&
          this.isSimilarResponseText(
            normalizedMessage,
            normalizedRenderedResponse
          );

        if (!isSimilarToOriginal && !isSimilarToRendered) {
          return;
        }

        suggestTags.forEach(tag => {
          const cleanedTag = String(tag || '').trim();
          if (!cleanedTag) return;
          if (this.conversationLabels.includes(cleanedTag)) return;
          matchedTags.add(cleanedTag);
        });
      });

      return [...matchedTags].slice(0, 6);
    },
    stickySuggestedTagsForConversation() {
      return (this.stickySuggestedTags || [])
        .map(tag => String(tag || '').trim())
        .filter(Boolean)
        .filter(tag => !this.conversationLabels.includes(tag))
        .slice(0, 6);
    },
    displayedSuggestedTags() {
      if (this.suggestedTagsForCurrentMessage.length > 0) {
        return this.suggestedTagsForCurrentMessage;
      }
      return this.stickySuggestedTagsForConversation;
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        this.stickySuggestedTags = [];
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (this.forcePrivateNoteByAssignment) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
        this.fetchAndSetReplyTo();
        return;
      }

      if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
        this.stickySuggestedTags = [];
      }
    },
    showCannedPicker(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.updateCannedPickerAnchor());
        window.addEventListener('scroll', this.updateCannedPickerAnchor, true);
        window.addEventListener('resize', this.updateCannedPickerAnchor);
      } else {
        window.removeEventListener(
          'scroll',
          this.updateCannedPickerAnchor,
          true
        );
        window.removeEventListener('resize', this.updateCannedPickerAnchor);
      }
    },
    message(updatedMessage) {
      // Check if the message starts with a slash.
      const bodyWithoutSignature = removeSignature(
        updatedMessage,
        this.signatureToApply
      );
      const startsWithSlash = bodyWithoutSignature.startsWith('/');
      const isSlashCommand = startsWithSlash && !this.showRichContentEditor;
      const slashSearchKey = isSlashCommand
        ? bodyWithoutSignature.substring(1)
        : '';

      // Determine if the user is potentially typing a slash command.
      // This is true if the message starts with a slash and the rich content editor is not active.
      this.hasSlashCommand = isSlashCommand;
      this.showMentions = this.hasSlashCommand && !this.showCannedPicker;

      // If a slash command is active, extract the command text after the slash.
      // If not, reset the mentionSearchKey.
      this.mentionSearchKey = slashSearchKey;

      // Unify `/` behavior with the canned button:
      // opening slash command should show the same canned picker popup.
      if (isSlashCommand) {
        this.openCannedPickerFromSlash(slashSearchKey);
      } else if (this.cannedPickerTriggeredBySlash && this.showCannedPicker) {
        this.closeCannedPicker();
      }

      // Autosave the current message draft.
      this.doAutoSaveDraft();

      // LiveChat(WebWidget) only: publish sneak peek while the agent is typing.
      if (this.isFocused && this.isAWebWidgetInbox && !this.isPrivate) {
        const preview = extractTextFromMarkdown(bodyWithoutSignature)
          .replace(/\s+/g, ' ')
          .trim();
        if (preview) {
          this.publishTypingPreviewDebounced(preview);
        }
      }
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
    forcePrivateNoteByAssignment(newValue) {
      if (newValue && this.replyType !== REPLY_EDITOR_MODES.NOTE) {
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
      }
    },
  },

  mounted() {
    this.$store.dispatch('getCannedResponse', {});
    this.$store.dispatch('labels/get').catch(() => {});
    this.cannedPickerShowAllUngroupedLocal = Boolean(
      this.uiSettings?.wlab_canned_picker_show_all_ungrouped
    );
    this.cannedPickerKeywordModeLocal = Boolean(
      this.uiSettings?.wlab_canned_picker_keyword_mode
    );
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    // LiveChat(WebWidget) only: publish "sneak peek" of agent typing content.
    // This is used by the dashboard conversation list + message view to monitor
    // what another CS is currently typing (3rd-party channels excluded).
    this.publishTypingPreviewDebounced = debounce(
      preview => {
        const conversationId = this.currentChat?.id;
        if (!conversationId) return;
        if (!this.isAWebWidgetInbox) return;
        if (this.isPrivate) return;
        this.$store.dispatch('conversationTypingStatus/toggleTyping', {
          status: 'on',
          conversationId,
          isPrivate: this.isPrivate,
          typingPreview: String(preview || '').slice(0, 280),
        });
      },
      350,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);

    // AI Assistant事件监听
    document.addEventListener('insert-ai-reply', this.handleInsertAiReply);
    document.addEventListener('send-ai-reply', this.handleSendAiReply);

    // Pre-warm emoji picker during browser idle time so first open feels instant.
    const mountEmojiPicker = () => {
      this.emojiPickerMounted = true;
      this.emojiPickerWarmupTimer = null;
      this.emojiPickerWarmupIdleId = null;
    };

    if (
      typeof window !== 'undefined' &&
      typeof window.requestIdleCallback === 'function'
    ) {
      this.emojiPickerWarmupIdleId = window.requestIdleCallback(
        mountEmojiPicker,
        { timeout: 700 }
      );
    } else {
      this.emojiPickerWarmupTimer = window.setTimeout(mountEmojiPicker, 80);
    }
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );

    // 移除AI Assistant事件监听
    document.removeEventListener('insert-ai-reply', this.handleInsertAiReply);
    document.removeEventListener('send-ai-reply', this.handleSendAiReply);

    if (this.emojiPickerWarmupTimer) {
      clearTimeout(this.emojiPickerWarmupTimer);
      this.emojiPickerWarmupTimer = null;
    }
    if (
      this.emojiPickerWarmupIdleId &&
      typeof window !== 'undefined' &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(this.emojiPickerWarmupIdleId);
      this.emojiPickerWarmupIdleId = null;
    }
  },
  methods: {
    updateStickySuggestedTagsAfterSend(sentSuggestedTags = []) {
      const normalized = Array.from(new Set(sentSuggestedTags))
        .map(tag => String(tag || '').trim())
        .filter(Boolean)
        .slice(0, 6);
      this.stickySuggestedTags = normalized;
    },
    handleInsert(article) {
      const { url, title } = article;
      if (this.isRichEditorEnabled) {
        // Removing empty lines from the title
        const lines = title.split('\n');
        const nonEmptyLines = lines.filter(line => line.trim() !== '');
        const filteredMarkdown = nonEmptyLines.join(' ');
        emitter.emit(
          BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
          `[${filteredMarkdown}](${url})`
        );
      } else {
        this.addIntoEditor(
          `${this.$t('CONVERSATION.REPLYBOX.INSERT_READ_MORE')} ${url}`
        );
      }

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });

      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);

      if (!this.showRichContentEditor && this.messageSignature) {
        // remove the old signature -> extract text from markdown -> attach new signature
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);

        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
            this.hideMentions();
            this.closeCannedPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showCannedPicker &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.onFileUpload({ name, type, size, file: file });
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    async confirmOnSendReply() {
      if (this.isReplyButtonDisabled || this.showMentions) return;

      const messageToSend = this.message;
      const sentSuggestedTags = [...this.suggestedTagsForCurrentMessage];
      const isOnWhatsApp =
        this.isATwilioWhatsAppChannel ||
        this.isAWhatsAppCloudChannel ||
        this.is360DialogWhatsAppChannel;
      // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
      // Although Chatwoot combines these into a single message, Instagram sends separate echo events for each component.
      // This can create duplicate messages in Chatwoot. To prevent this issue, we'll handle text and attachments as separate messages.
      const isOnInstagram = this.isAnInstagramChannel;
      const shouldSplitOutgoing =
        (isOnWhatsApp || isOnInstagram) && !this.isPrivate;
      const messagePayloads = shouldSplitOutgoing
        ? this.getMultipleMessagesPayload(messageToSend)
        : [this.getMessagePayload(messageToSend)];

      if (!messagePayloads.length) return;

      // Clear composer immediately so users can continue typing/sending while
      // network/channel delivery is still in flight.
      if (!this.isPrivate) {
        this.clearEmailField();
      }
      this.clearMessage();
      this.hideEmojiPicker();
      this.$emit('update:popOutReplyBox', false);

      await this.sendMessageAsMultipleMessages(messagePayloads);
      this.updateStickySuggestedTagsAfterSend(sentSuggestedTags);
    },
    async sendMessageAsMultipleMessages(messagePayloads = []) {
      for (const messagePayload of messagePayloads) {
        // eslint-disable-next-line no-await-in-loop
        await this.sendMessage(messagePayload);
      }
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          await this.confirmOnSendReply();
        }
      } else {
        await this.confirmOnSendReply();
      }
    },
    buildLineEmojiToken({ productId, emojiId }) {
      const safeProductId = String(productId || '').trim();
      const safeEmojiId = String(emojiId || '').padStart(3, '0');
      if (!safeProductId || !safeEmojiId) return '';

      return `[line_emoji:${safeProductId}:${safeEmojiId}]`;
    },
    onSendLineEmoji({ productId, emojiId }) {
      const token = this.buildLineEmojiToken({ productId, emojiId });
      if (!token) return;
      this.addIntoEditor(token);
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(message) {
      message = String(message || '');
      if (this.sendWithSignature && !this.private) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        message = appendSignature(message, this.signatureToApply);
      }

      // First convert simplified syntax [name] to {{contact.name}}
      const messageWithStandardVars = replaceSimplifiedVariables(message);

      // Then replace variables with actual values
      const updatedMessage = replaceVariablesInMessage({
        message: messageWithStandardVars,
        variables: this.messageVariables,
      });

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    updateCannedPickerAnchor() {
      const el = this.replyEditor?.value ?? this.replyEditor;
      if (!el?.getBoundingClientRect) return;
      const rect = el.getBoundingClientRect();
      this.cannedPickerAnchorX = rect.left;
      this.cannedPickerAnchorY = rect.top - 8;
    },
    openCannedPickerFromSlash(searchKey = '') {
      this.cannedSearchQuery = searchKey;
      if (!this.showCannedPicker) {
        this.showCannedPicker = true;
        this.cannedPickerTriggeredBySlash = true;
        this.showMentions = false;
        this.cannedPickerSelectedShortCode = '';
        this.showCannedPickerSettings = false;
        this.cannedPickerShowAllUngroupedLocal = Boolean(
          this.uiSettings?.wlab_canned_picker_show_all_ungrouped
        );
        this.cannedPickerKeywordModeLocal = Boolean(
          this.uiSettings?.wlab_canned_picker_keyword_mode
        );
        this.$store.dispatch('getCannedResponse', {});
        this.updateCannedPickerAnchor();
      }
    },
    toggleCannedPicker() {
      this.showCannedPicker = !this.showCannedPicker;
      if (this.showCannedPicker) {
        this.cannedPickerTriggeredBySlash = false;
        this.cannedSearchQuery = '';
        this.cannedPickerSelectedShortCode = '';
        this.showCannedPickerSettings = false;
        this.cannedPickerShowAllUngroupedLocal = Boolean(
          this.uiSettings?.wlab_canned_picker_show_all_ungrouped
        );
        this.cannedPickerKeywordModeLocal = Boolean(
          this.uiSettings?.wlab_canned_picker_keyword_mode
        );
        this.$store.dispatch('getCannedResponse', {});
        this.updateCannedPickerAnchor();

        const bodyWithoutSignature = removeSignature(
          this.message || '',
          this.signatureToApply
        );
        if (!this.showRichContentEditor && !bodyWithoutSignature.trim()) {
          const base = '/';
          this.message =
            this.sendWithSignature && !this.isPrivate
              ? appendSignature(base, this.signatureToApply)
              : base;
          this.$nextTick(() => this.$refs.messageInput?.$el?.focus?.());
        }
      } else {
        this.cannedPickerSelectedShortCode = '';
        this.showCannedPickerSettings = false;
        this.cannedPickerTriggeredBySlash = false;
      }
    },
    closeCannedPicker() {
      this.showCannedPicker = false;
      this.cannedPickerSelectedShortCode = '';
      this.showCannedPickerSettings = false;
      this.cannedPickerTriggeredBySlash = false;
    },
    selectCannedResponse(response) {
      this.closeCannedPicker();
      this.attachCannedMedia(response);
      this.replaceText(response?.content || '');
    },
    selectCannedShortCode(shortCode) {
      this.cannedPickerSelectedShortCode = (shortCode || '').trim();
    },
    backToCannedKeywords() {
      this.cannedPickerSelectedShortCode = '';
    },
    openAddCannedModal() {
      this.showAddCannedModal = true;
      this.closeCannedPicker();
    },
    closeAddCannedModal() {
      this.showAddCannedModal = false;
    },
    goToCannedSettingsPage() {
      this.closeCannedPicker();
      this.$router.push({ name: 'canned_list' });
    },
    normalizeTextForSuggestion(text = '') {
      return String(text)
        .toLowerCase()
        .replace(/\{\{[^}]+\}\}/g, ' ')
        .replace(/[^a-z0-9\u4e00-\u9fff]+/gi, '');
    },
    levenshteinDistance(source = '', target = '') {
      const s = source.slice(0, 260);
      const t = target.slice(0, 260);
      const sourceLength = s.length;
      const targetLength = t.length;
      if (!sourceLength) return targetLength;
      if (!targetLength) return sourceLength;

      const distance = Array.from({ length: sourceLength + 1 }, () =>
        new Array(targetLength + 1).fill(0)
      );

      for (let i = 0; i <= sourceLength; i += 1) distance[i][0] = i;
      for (let j = 0; j <= targetLength; j += 1) distance[0][j] = j;

      for (let i = 1; i <= sourceLength; i += 1) {
        for (let j = 1; j <= targetLength; j += 1) {
          const substitutionCost = s[i - 1] === t[j - 1] ? 0 : 1;
          distance[i][j] = Math.min(
            distance[i - 1][j] + 1,
            distance[i][j - 1] + 1,
            distance[i - 1][j - 1] + substitutionCost
          );
        }
      }

      return distance[sourceLength][targetLength];
    },
    isSimilarResponseText(inputText = '', cannedText = '') {
      if (!inputText || !cannedText) return false;
      if (inputText === cannedText) return true;
      if (inputText.includes(cannedText) || cannedText.includes(inputText)) {
        return true;
      }

      const maxLength = Math.max(inputText.length, cannedText.length);
      if (!maxLength) return false;

      const lengthDifferenceRatio =
        Math.abs(inputText.length - cannedText.length) / maxLength;
      if (lengthDifferenceRatio > 0.45) return false;

      const distance = this.levenshteinDistance(inputText, cannedText);
      const similarityScore = 1 - distance / maxLength;
      return similarityScore >= 0.86;
    },
    async applySuggestedTag(labelTitle) {
      const tag = String(labelTitle || '').trim();
      if (!tag || this.conversationLabels.includes(tag)) return;

      const currentLabels = [...this.conversationLabels, tag];
      try {
        await this.$store.dispatch('conversationLabels/update', {
          conversationId: this.currentChat.id,
          labels: currentLabels,
        });
        this.recentlyAppliedSuggestedTag = tag;
        window.setTimeout(() => {
          if (this.recentlyAppliedSuggestedTag === tag) {
            this.recentlyAppliedSuggestedTag = '';
          }
        }, 700);
      } catch (error) {
        useAlert(error?.message || 'Failed to apply suggested tag.');
      }
    },
    async fetchCannedMediaAsFile(media = {}) {
      const mediaUrl = media.url;
      if (!mediaUrl) return null;

      const response = await fetch(mediaUrl, {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error('Unable to download canned media');
      }
      const blob = await response.blob();
      return new File([blob], media.filename || `canned-media-${Date.now()}`, {
        type: media.content_type || blob.type || 'application/octet-stream',
      });
    },
    async buildAttachmentFromCannedMedia(media = {}) {
      const mediaType = String(media.content_type || '');
      const baseResource = {
        id: media.id || media.signed_id,
        filename: media.filename,
        name: media.filename,
        content_type: mediaType,
        type: mediaType,
        byte_size: media.byte_size,
        size: media.byte_size,
      };

      const attachment = {
        currentChatId: this.currentChat?.id,
        isPrivate: this.isPrivate,
        thumb: mediaType.startsWith('image/') ? media.url : '',
        resource: baseResource,
        blobSignedId: media.signed_id,
        isRecordedAudio: false,
      };

      if (this.globalConfig.directUploadsEnabled) {
        return attachment;
      }

      const file = await this.fetchCannedMediaAsFile(media);
      if (!file) return null;
      return {
        ...attachment,
        resource: {
          ...baseResource,
          file,
        },
      };
    },
    async attachCannedMedia(response = {}) {
      const medias = Array.isArray(response?.media_attachments)
        ? response.media_attachments
        : [];
      if (!medias.length) return;

      const existingSignedIds = new Set(
        this.attachedFiles
          .map(attachment => attachment.blobSignedId)
          .filter(Boolean)
      );
      const newAttachments = [];

      for (const media of medias) {
        if (media?.signed_id && existingSignedIds.has(media.signed_id)) {
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const attachment = await this.buildAttachmentFromCannedMedia(media);
          if (!attachment) continue;
          newAttachments.push(attachment);
          if (media?.signed_id) {
            existingSignedIds.add(media.signed_id);
          }
        } catch (error) {
          useAlert(error?.message || 'Unable to attach canned media.');
        }
      }

      if (newAttachments.length) {
        this.attachedFiles = [...this.attachedFiles, ...newAttachments];
      }
    },
    goToLabelSettingsPage() {
      this.showTagPicker = false;
      const accountId = this.$route.params.accountId;
      this.$router.push({
        name: 'labels_list',
        params: { accountId },
      });
    },
    addTagToConversation(label) {
      const currentLabels = [...this.conversationLabels];
      if (!currentLabels.includes(label.title)) {
        currentLabels.push(label.title);
        this.$store.dispatch('conversationLabels/update', {
          conversationId: this.currentChat.id,
          labels: currentLabels,
        });
      }
      this.showTagPicker = false;
    },
    removeTagFromConversation(labelTitle) {
      const currentLabels = this.conversationLabels.filter(
        l => l !== labelTitle
      );
      this.$store.dispatch('conversationLabels/update', {
        conversationId: this.currentChat.id,
        labels: currentLabels,
      });
    },
    filteredLabelsForPicker() {
      const q = (this.tagSearchQuery || '').toLowerCase().trim();
      return this.accountLabels.filter(label => {
        if (this.conversationLabels.includes(label.title)) return false;
        if (q && !label.title.toLowerCase().includes(q)) return false;
        return true;
      });
    },
    toggleReplyMode() {
      if (this.forcePrivateNoteByAssignment) {
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
        return;
      }
      const newMode =
        this.replyType === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      this.setReplyMode(newMode);
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      const effectiveMode = this.forcePrivateNoteByAssignment
        ? REPLY_EDITOR_MODES.NOTE
        : mode;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode: effectiveMode,
      });
      if (this.forcePrivateNoteByAssignment) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      } else if (canReply || this.isAWhatsAppChannel) {
        this.replyType = effectiveMode;
      }
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    escapeHtml(rawText = '') {
      return String(rawText)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    buildLineEmojiComposerPreviewHtml(rawText = '') {
      const escaped = this.escapeHtml(rawText);
      const withEmojiImages = escaped.replace(
        LINE_EMOJI_TOKEN_REGEX,
        (_match, productId, emojiId) => {
          const src = buildLineEmojiImageUrl({ productId, emojiId });
          if (!src) return _match;
          return `<img class="wlab-line-emoji-composer__img" src="${src}" alt="line emoji" />`;
        }
      );

      return withEmojiImages.replace(/\n/g, '<br />');
    },
    insertIntoTextEditor(text, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        text +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    addIntoEditor(content) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = content;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const inputEl = this.$refs.messageInput?.$el;
        const defaultCursorPos = this.message.length;
        const selectionStart = Number.isInteger(inputEl?.selectionStart)
          ? inputEl.selectionStart
          : defaultCursorPos;
        const selectionEnd = Number.isInteger(inputEl?.selectionEnd)
          ? inputEl.selectionEnd
          : selectionStart;
        this.insertIntoTextEditor(content, selectionStart, selectionEnd);
        const nextCursorPos = selectionStart + content.length;
        this.$nextTick(() => {
          inputEl?.focus?.();
          inputEl?.setSelectionRange?.(nextCursorPos, nextCursorPos);
          this.$refs.messageInput?.resizeTextarea?.();
        });
      }
    },
    // AI Assistant: 插入建议到输入框
    handleInsertAiReply(event) {
      const text = event.detail?.text || '';
      if (text) {
        this.message = text;
        this.onFocus();
        this.$nextTick(() => {
          if (this.$refs.messageInput?.resizeTextarea) {
            this.$refs.messageInput.resizeTextarea();
          }
        });
      }
    },
    // AI Assistant: 直接发送建议
    handleSendAiReply(event) {
      const text = event.detail?.text || '';
      if (text) {
        const messagePayload = this.getMessagePayload(text);
        this.sendMessage(messagePayload);
      }
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        this.message = appendSignature(this.message, this.signatureToApply);
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      if (!this.emojiPickerMounted) {
        this.emojiPickerMounted = true;
      }
      if (this.emojiPickerWarmupTimer) {
        clearTimeout(this.emojiPickerWarmupTimer);
        this.emojiPickerWarmupTimer = null;
      }
      if (
        this.emojiPickerWarmupIdleId &&
        typeof window !== 'undefined' &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(this.emojiPickerWarmupIdleId);
        this.emojiPickerWarmupIdleId = null;
      }

      // toggleEmojiPicker can be invoked with an event from the toolbar button
      // so we can anchor the popover reliably.
      let x = 16;
      let bottom = 80;
      const evt = arguments?.[0];
      const targetEl = evt?.currentTarget || evt?.target;
      const replyEl = this.$refs?.replyEditor;
      const rect =
        typeof targetEl?.getBoundingClientRect === 'function'
          ? targetEl.getBoundingClientRect()
          : typeof replyEl?.getBoundingClientRect === 'function'
            ? replyEl.getBoundingClientRect()
            : null;

      if (rect) {
        x = rect.left;
        bottom = Math.max(10, window.innerHeight - rect.bottom + 56);
      }

      // Clamp within viewport width (picker is 312px)
      const maxLeft = Math.max(10, window.innerWidth - 312 - 10);
      this.emojiPickerAnchorX = Math.min(Math.max(10, Math.round(x)), maxLeft);
      this.emojiPickerAnchorBottom = Math.round(bottom);

      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.isRecordingAudio) {
        return;
      }
      if (!this.isRecorderAudioStopped) {
        this.isRecorderAudioStopped = true;
        this.$refs.audioRecorderInput.stopRecording();
      } else if (this.isRecorderAudioStopped) {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.showEmojiPicker = false;
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
        typingPreview:
          status === 'on' && this.isAWebWidgetInbox && !isPrivate
            ? extractTextFromMarkdown(
                removeSignature(this.message, this.signatureToApply)
              )
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 280)
            : '',
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = this.isAnInstagramChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram, we need a separate text message
      // For WhatsApp, we only need a text message if there are no attachments
      if (
        (this.isAnInstagramChannel && this.message) ||
        (!this.isAnInstagramChannel && hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }

      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <!-- Top panel hidden: mode toggle moved into bottom toolbar -->
    <!-- Character limit warning shown inline when needed -->
    <div
      v-if="isMessageLengthReachingThreshold"
      class="flex items-center justify-end px-4 pt-2"
    >
      <span
        class="text-xs"
        :class="charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11'"
      >
        {{
          charactersRemaining < 0
            ? `${-charactersRemaining} characters over limit`
            : `${charactersRemaining} characters remaining`
        }}
      </span>
    </div>
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <div class="reply-box__top">
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <CannedResponse
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @replace="replaceText"
      />
      <Teleport to="body">
        <div
          v-if="emojiPickerMounted"
          v-on-clickaway="hideEmojiPicker"
          class="wlab-emoji-picker-popover"
          :style="emojiPickerStyle"
          :class="{ 'wlab-emoji-picker-popover--open': showEmojiPicker }"
          @click.stop
        >
          <WlabEmojiPicker :on-select="addIntoEditor" />
        </div>
      </Teleport>
      <ReplyEmailHead
        v-if="showReplyHead"
        v-model:cc-emails="ccEmails"
        v-model:bcc-emails="bccEmails"
        v-model:to-emails="toEmails"
      />
      <AudioRecorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @recorder-progress-changed="onRecordProgressChanged"
        @finish-record="onFinishRecorder"
        @play="recordingAudioState = 'playing'"
        @pause="recordingAudioState = 'paused'"
      />
      <div
        v-else-if="!showRichContentEditor"
        class="wlab-line-emoji-composer"
        :class="{
          'wlab-line-emoji-composer--has-token':
            hasRenderableLineEmojiComposerPreview,
        }"
      >
        <div
          v-if="hasRenderableLineEmojiComposerPreview"
          class="wlab-line-emoji-composer__preview"
          v-html="lineEmojiComposerPreviewHtml"
        />
        <ResizableTextArea
          ref="messageInput"
          v-model="message"
          class="rounded-none input"
          :class="{
            'wlab-line-emoji-composer__textarea':
              hasRenderableLineEmojiComposerPreview,
          }"
          :placeholder="messagePlaceHolder"
          :min-height="4"
          :signature="signatureToApply"
          allow-signature
          :send-with-signature="sendWithSignature"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>
      <WootMessageEditor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :hide-menubar="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        enable-variables
        :variables="messageVariables"
        :signature="signatureToApply"
        allow-signature
        :channel-type="channelType"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
      />
    </div>
    <div
      v-if="hasAttachments && !showAudioRecorderEditor"
      class="attachment-preview-box"
      @paste="onPaste"
    >
      <AttachmentPreview
        class="flex-col mt-4"
        :attachments="attachedFiles"
        @remove-attachment="removeAttachment"
      />
    </div>
    <MessageSignatureMissingAlert
      v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
    />
    <ReplyBottomPanel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :enable-whats-app-templates="showWhatsappTemplates"
      :enable-content-templates="showContentTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :is-note="isPrivate"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :conversation-type="conversationType"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :message="message"
      :portal-slug="connectedPortalSlug"
      :new-conversation-modal-active="newConversationModalActive"
      :line-user-id="lineUserId"
      :lock-to-private-note="forcePrivateNoteByAssignment"
      @select-whatsapp-template="openWhatsappTemplateModal"
      @select-content-template="openContentTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @toggle-insert-article="toggleInsertArticle"
      @toggle-mode="toggleReplyMode"
      @toggle-canned-picker="toggleCannedPicker"
      @send-line-emoji="onSendLineEmoji"
    />

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>

  <!-- Canned Responses Picker (popover, anchored to reply box) -->
  <div
    v-if="showCannedPicker"
    v-on-clickaway="closeCannedPicker"
    class="wlab-canned-picker"
    :style="cannedPickerStyle"
    data-testid="canned-responses-dropdown"
  >
    <div class="wlab-canned-picker__list">
      <!-- Detail view -->
      <template v-if="cannedPickerKeywordMode && cannedPickerSelectedShortCode">
        <div class="wlab-canned-picker__detail-head">
          <button
            class="wlab-canned-picker__back"
            type="button"
            @click.stop="backToCannedKeywords"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m15 18-6-6 6-6"
              />
            </svg>
          </button>
          <div class="wlab-canned-picker__detail-title">
            {{ cannedPickerSelectedShortCode }}
          </div>
        </div>
        <div
          v-for="response in cannedPickerDetailResponses"
          :key="response.id"
          class="wlab-canned-picker__item wlab-canned-picker__item--detail"
          @click.stop="selectCannedResponse(response)"
        >
          <div class="wlab-canned-picker__label">
            <div class="wlab-canned-picker__shortcode">
              {{ response.short_code }}
            </div>
            <div class="wlab-canned-picker__preview">
              {{ response.content }}
            </div>
          </div>
          <span class="wlab-canned-picker__arrow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m9 6 6 6-6 6"
              />
            </svg>
          </span>
        </div>
        <div
          v-if="cannedPickerDetailResponses.length === 0"
          class="wlab-canned-picker__empty"
        >
          No canned responses found
        </div>
      </template>

      <!-- Keyword list -->
      <template v-else-if="cannedPickerKeywordMode">
        <template
          v-for="(keywords, groupName) in cannedPickerKeywordsByGroup"
          :key="groupName"
        >
          <div
            v-for="(item, idx) in keywords"
            :key="`${groupName}-${item.short_code}`"
            class="wlab-canned-picker__item"
            :class="{
              'wlab-canned-picker__item--group-first':
                !cannedPickerShowAllUngrouped && idx === 0,
            }"
            :data-group="
              !cannedPickerShowAllUngrouped && idx === 0 ? groupName : undefined
            "
            :data-testid="`shortcut-item-${item.short_code}`"
            @click.stop="selectCannedShortCode(item.short_code)"
          >
            <div
              data-testid="shortcut-name"
              class="wlab-canned-picker__shortcode"
            >
              {{ item.short_code }}
            </div>
            <div
              data-testid="canned-responses-count"
              class="wlab-canned-picker__count"
            >
              {{ item.count }}
            </div>
            <span
              data-test="shortcut-select-button"
              class="wlab-canned-picker__arrow"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m9 6 6 6-6 6"
                />
              </svg>
            </span>
          </div>
        </template>
        <div
          v-if="Object.keys(cannedPickerKeywordsByGroup).length === 0"
          class="wlab-canned-picker__empty"
        >
          No canned responses found
        </div>
      </template>

      <!-- Flat list -->
      <template v-else>
        <template
          v-for="(items, groupName) in cannedPickerGroupedView"
          :key="groupName"
        >
          <div
            v-for="(response, idx) in items"
            :key="response.id"
            class="wlab-canned-picker__item"
            :class="{
              'wlab-canned-picker__item--group-first':
                !cannedPickerShowAllUngrouped && idx === 0,
            }"
            :data-group="
              !cannedPickerShowAllUngrouped && idx === 0 ? groupName : undefined
            "
            @click.stop="selectCannedResponse(response)"
          >
            <div class="wlab-canned-picker__label">
              <div class="wlab-canned-picker__shortcode">
                {{ response.short_code }}
              </div>
              <div class="wlab-canned-picker__preview">
                {{ response.content }}
              </div>
            </div>
            <span class="wlab-canned-picker__arrow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m9 6 6 6-6 6"
                />
              </svg>
            </span>
          </div>
        </template>
        <div
          v-if="filteredCannedRecords.length === 0"
          class="wlab-canned-picker__empty"
        >
          No canned responses found
        </div>
      </template>
    </div>

    <div class="wlab-canned-picker__footer">
      <button
        type="button"
        class="wlab-canned-picker__new"
        @click="openAddCannedModal"
      >
        <span>+ New</span>
      </button>

      <div class="wlab-canned-picker__settings-wrap">
        <button
          type="button"
          data-testid="canned-responses-settings"
          class="wlab-canned-picker__settings"
          @click.stop="showCannedPickerSettings = !showCannedPickerSettings"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="20"
            height="20"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12.675 3a1.8 1.8 0 0 1 1.8 1.8v1.407c.462.197.9.45 1.305.753l1.218-.703a1.8 1.8 0 0 1 2.459.658l.675 1.17a1.8 1.8 0 0 1-.66 2.458l-1.217.704a6.3 6.3 0 0 1 0 1.507l1.218.703a1.8 1.8 0 0 1 .659 2.458l-.675 1.17a1.8 1.8 0 0 1-2.46.658l-1.217-.703a6.3 6.3 0 0 1-1.305.754V19.2a1.8 1.8 0 0 1-1.8 1.8h-1.35a1.8 1.8 0 0 1-1.8-1.8v-1.406a6.3 6.3 0 0 1-1.305-.754l-1.218.703a1.8 1.8 0 0 1-2.46-.658l-.674-1.17a1.8 1.8 0 0 1 .659-2.458l1.218-.704a6.3 6.3 0 0 1 0-1.506l-1.218-.704a1.8 1.8 0 0 1-.66-2.458l.676-1.17a1.8 1.8 0 0 1 2.459-.658l1.218.703a6.3 6.3 0 0 1 1.305-.753V4.8a1.8 1.8 0 0 1 1.8-1.8z"
              clip-rule="evenodd"
            />
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M14.696 12a2.696 2.696 0 1 1-5.392 0 2.696 2.696 0 0 1 5.392 0"
            />
          </svg>
        </button>

        <div
          v-if="showCannedPickerSettings"
          v-on-clickaway="() => (showCannedPickerSettings = false)"
          class="wlab-canned-settings-popover"
        >
          <div class="wlab-canned-settings-popover__row">
            <div class="wlab-canned-settings-popover__label">
              Show all responses
            </div>
            <Switch
              v-model="cannedPickerShowAllUngroupedLocal"
              @change="
                updateUISettings({
                  wlab_canned_picker_show_all_ungrouped:
                    cannedPickerShowAllUngroupedLocal,
                })
              "
            />
          </div>
          <div class="wlab-canned-settings-popover__row">
            <div class="wlab-canned-settings-popover__label">
              Allow multiple replies per keyword
            </div>
            <Switch
              v-model="cannedPickerKeywordModeLocal"
              @change="
                cannedPickerSelectedShortCode = '';
                updateUISettings({
                  wlab_canned_picker_keyword_mode: cannedPickerKeywordModeLocal,
                });
              "
            />
          </div>
          <div class="wlab-canned-settings-popover__actions">
            <button
              type="button"
              class="wlab-canned-settings-popover__manage"
              @click="goToCannedSettingsPage"
            >
              Manage response
            </button>
            <button
              type="button"
              class="wlab-canned-settings-popover__gear"
              @click="goToCannedSettingsPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12.675 3a1.8 1.8 0 0 1 1.8 1.8v1.407c.462.197.9.45 1.305.753l1.218-.703a1.8 1.8 0 0 1 2.459.658l.675 1.17a1.8 1.8 0 0 1-.66 2.458l-1.217.704a6.3 6.3 0 0 1 0 1.507l1.218.703a1.8 1.8 0 0 1 .659 2.458l-.675 1.17a1.8 1.8 0 0 1-2.46.658l-1.217-.703a6.3 6.3 0 0 1-1.305.754V19.2a1.8 1.8 0 0 1-1.8 1.8h-1.35a1.8 1.8 0 0 1-1.8-1.8v-1.406a6.3 6.3 0 0 1-1.305-.754l-1.218.703a1.8 1.8 0 0 1-2.46-.658l-.674-1.17a1.8 1.8 0 0 1 .659-2.458l1.218-.704a6.3 6.3 0 0 1 0-1.506l-1.218-.704a1.8 1.8 0 0 1-.66-2.458l.676-1.17a1.8 1.8 0 0 1 2.459-.658l1.218.703a6.3 6.3 0 0 1 1.305-.753V4.8a1.8 1.8 0 0 1 1.8-1.8z"
                  clip-rule="evenodd"
                />
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.696 12a2.696 2.696 0 1 1-5.392 0 2.696 2.696 0 0 1 5.392 0"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AddCanned v-if="showAddCannedModal" :on-close="closeAddCannedModal" />

  <!-- Tag picker (below reply box) -->
  <div class="wlab-tag-bar">
    <div class="wlab-tag-bar__tags">
      <!-- Active tags -->
      <span
        v-for="label in conversationLabels"
        :key="label"
        class="wlab-tag-bar__tag"
      >
        {{ label }}
        <button
          class="wlab-tag-bar__tag-remove"
          @click="removeTagFromConversation(label)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="12"
            height="12"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m6 6 12 12m0-12L6 18"
            />
          </svg>
        </button>
      </span>
      <button
        v-for="tag in displayedSuggestedTags"
        :key="`suggest-${tag}`"
        type="button"
        class="wlab-tag-bar__tag wlab-tag-bar__tag--suggested"
        :class="{
          'wlab-tag-bar__tag--suggested-applied':
            recentlyAppliedSuggestedTag === tag,
        }"
        @click="applySuggestedTag(tag)"
      >
        <span class="wlab-tag-bar__suggest-pill">Suggest</span>
        <span>{{ tag }}</span>
      </button>
      <!-- Add tag button -->
      <div
        v-on-clickaway="() => (showTagPicker = false)"
        class="wlab-tag-bar__picker-wrap"
      >
        <button
          class="wlab-tag-bar__add-btn"
          type="button"
          @click="showTagPicker = !showTagPicker"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path
              fill="currentColor"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8.5 9.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2"
            />
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 7v3.859c0 .537.213 1.052.593 1.432l8.116 8.116a2.023 2.023 0 0 0 2.864 0l4.834-4.834a2.023 2.023 0 0 0 0-2.864L12.29 4.593A2.03 2.03 0 0 0 10.859 4H7a3 3 0 0 0-3 3"
            />
          </svg>
          <span>Add tag</span>
        </button>
        <!-- Tag picker dropdown (LiveChat-style) -->
        <div
          v-if="showTagPicker"
          class="wlab-tag-picker"
          data-testid="tag-picker-dropdown"
        >
          <!-- Tag list -->
          <div class="wlab-tag-picker__list">
            <div
              v-for="(label, index) in filteredLabelsForPicker()"
              :key="label.id"
              class="wlab-tag-picker__item"
              :title="label.title"
              data-testid="tag-picker-list-item"
              :data-tag-name="label.title"
              :data-tag-index="index"
              @click="addTagToConversation(label)"
            >
              <div class="wlab-tag-picker__item-left">
                <button
                  type="button"
                  class="wlab-tag-picker__pin-btn"
                  data-testid="tag-picker-pin-tag"
                  @click.stop.prevent
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                  >
                    <g
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    >
                      <path d="M9 4v6l-2 4v2h10v-2l-2-4V4M12 16v5M8 4h8" />
                    </g>
                  </svg>
                </button>
                <p class="wlab-tag-picker__name">{{ label.title }}</p>
              </div>
            </div>
            <div
              v-if="filteredLabelsForPicker().length === 0"
              class="wlab-tag-picker__empty"
            >
              No tags available
            </div>
          </div>
          <!-- Bottom row: search + settings icon -->
          <div class="wlab-tag-picker__bottom">
            <div class="wlab-tag-picker__search">
              <input
                v-model="tagSearchQuery"
                data-testid="tag-picker-search-bar"
                type="text"
                placeholder="Search tag"
                class="wlab-tag-picker__search-input"
              />
            </div>
            <button
              type="button"
              class="wlab-tag-picker__settings-btn"
              aria-label="Tag settings"
              @click.stop.prevent="goToLabelSettingsPage"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12.675 3a1.8 1.8 0 0 1 1.8 1.8v1.407c.462.197.9.45 1.305.753l1.218-.703a1.8 1.8 0 0 1 2.459.658l.675 1.17a1.8 1.8 0 0 1-.66 2.458l-1.217.704a6.3 6.3 0 0 1 0 1.507l1.218.703a1.8 1.8 0 0 1 .659 2.458l-.675 1.17a1.8 1.8 0 0 1-2.46.658l-1.217-.703a6.3 6.3 0 0 1-1.305.754V19.2a1.8 1.8 0 0 1-1.8 1.8h-1.35a1.8 1.8 0 0 1-1.8-1.8v-1.406a6.3 6.3 0 0 1-1.305-.754l-1.218.703a1.8 1.8 0 0 1-2.46-.658l-.674-1.17a1.8 1.8 0 0 1 .659-2.458l1.218-.704a6.3 6.3 0 0 1 0-1.506l-1.218-.704a1.8 1.8 0 0 1-.66-2.458l.676-1.17a1.8 1.8 0 0 1 2.459-.658l1.218.703a6.3 6.3 0 0 1 1.305-.753V4.8a1.8 1.8 0 0 1 1.8-1.8z"
                  clip-rule="evenodd"
                />
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.696 12a2.696 2.696 0 1 1-5.392 0 2.696 2.696 0 0 1 5.392 0"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  background: var(--wlab-bg-primary, #ffffff);
  border: 1px solid var(--wlab-border-default, #e2e2e4);
  border-radius: 16px;
  max-height: 280px;
  min-height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: clip;
  position: relative;
  margin: 0 16px 5px;

  &.is-private {
    border-color: #e5c27a;
    background: #fff2d6;
  }

  /* Same internal-note colors across light/dark/mix */

  &.is-focused {
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  position: relative;
  padding: 16px 16px 0;
  height: auto;
  min-height: 48px;
  overflow: auto;

  textarea {
    outline: none;
    border: none;
    background: transparent;
    margin: 0;
    max-height: 160px;
    min-height: 3rem;
    padding: 0;
    resize: none;
    width: 100%;
    font-size: 14px;
    line-height: 22px;
    color: var(--wlab-text-primary, #131317);
  }

  textarea:focus,
  textarea:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
  }
}

.wlab-emoji-picker-popover {
  user-select: none;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(4px) scale(0.98);
  transform-origin: bottom left;
  backface-visibility: hidden;
  will-change: transform, opacity;
  contain: layout paint;
  transition:
    opacity 0.12s ease,
    transform 0.12s ease,
    visibility 0.12s linear;
}

.wlab-emoji-picker-popover--open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateY(0) scale(1);
}

.normal-editor__canned-box {
  width: calc(100% - 2 * 1rem);
  left: 1rem;
}

.wlab-line-emoji-composer {
  position: relative;
}

.wlab-line-emoji-composer__preview {
  position: absolute;
  inset: 0;
  pointer-events: none;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 22px;
  color: var(--wlab-text-primary, #131317);
  z-index: 1;
}

.wlab-line-emoji-composer__preview :deep(.wlab-line-emoji-composer__img) {
  width: 16px;
  height: 16px;
  display: inline-block;
  object-fit: contain;
  vertical-align: text-bottom;
}

:deep(textarea.wlab-line-emoji-composer__textarea) {
  position: relative;
  z-index: 2;
  color: transparent !important;
  caret-color: var(--wlab-text-primary, #131317);
}

:deep(textarea.wlab-line-emoji-composer__textarea::placeholder) {
  color: #8d8d95 !important;
}

.wlab-tag-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 0px 10px;
}

.wlab-tag-bar__tags {
  display: flex;
  margin-left: 8px;
  align-items: center;
  position: relative;
  flex-wrap: wrap;
  min-height: 34px;
}

.wlab-tag-bar__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;

  &:hover {
    background: var(--wlab-bg-hover, #f1f5f9);
  }

  svg {
    flex-shrink: 0;
  }
}

/* Pure dark only (mix theme also has `.dark`, but should stay black icons) */
:global(body.dark:not(.theme-mix)) .wlab-tag-bar__add-btn,
:global(body.dark:not(.theme-mix)) .wlab-tag-bar__add-btn span,
:global(body.dark:not(.theme-mix)) .wlab-tag-bar__add-btn svg,
:global(body.dark:not(.theme-mix)) .wlab-tag-bar__add-btn svg * {
  color: #ffffff !important;
}

.wlab-tag-bar__picker-wrap {
  position: relative;
}

.wlab-tag-bar__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--wlab-bg-tertiary, #f1f5f9);
  color: var(--wlab-text-secondary, #475569);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  margin-right: 4px;
}

.wlab-tag-bar__tag--suggested {
  border: 1px solid #c7d2fe;
  background: linear-gradient(120deg, #eef2ff 0%, #e0f2fe 100%);
  color: #1e3a8a;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
  animation: wlabTagSuggestPulse 1.6s ease-in-out infinite;
}

.wlab-tag-bar__tag--suggested:hover {
  transform: translateY(-1px);
  border-color: #93c5fd;
  box-shadow: 0 4px 10px rgba(30, 58, 138, 0.14);
}

.wlab-tag-bar__tag--suggested-applied {
  animation: wlabTagSuggestPop 0.32s ease;
}

.wlab-tag-bar__suggest-pill {
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.18);
  color: #1d4ed8;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

@keyframes wlabTagSuggestPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
  }
}

@keyframes wlabTagSuggestPop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
}

:global(.dark) .wlab-tag-bar__tag {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

:global(.dark) .wlab-tag-bar__tag--suggested {
  border-color: rgba(147, 197, 253, 0.45);
  background: linear-gradient(
    120deg,
    rgba(30, 58, 138, 0.38) 0%,
    rgba(8, 47, 73, 0.5) 100%
  );
  color: #dbeafe;
}

:global(.dark) .wlab-tag-bar__tag--suggested .wlab-tag-bar__suggest-pill {
  background: rgba(147, 197, 253, 0.22);
  color: #bfdbfe;
}

.wlab-tag-bar__tag-remove {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}

.wlab-tag-picker {
  position: absolute;
  left: 0;
  bottom: 36px;
  width: 300px;
  background-color: var(--surface-primary-default, #ffffff);
  color: #131317;
  box-shadow: var(--shadow-pop-over, 0 6px 20px rgba(19, 19, 23, 0.2));
  display: flex;
  flex-direction: column;
  padding: 3px 0;
  box-sizing: border-box;
  z-index: 8000;
  border-radius: 4px;
}

:global(.dark) .wlab-tag-picker {
  background: #131317;
  color: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
}

.wlab-tag-picker__list {
  height: 216px;
  max-height: 352px;
  overflow-y: auto;
  position: relative;
  outline: none;
}

.wlab-tag-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    background: #ececec;
  }
}

:global(.dark) .wlab-tag-picker__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.wlab-tag-picker__item-left {
  display: flex;
  align-items: center;
}

.wlab-tag-picker__pin-btn {
  width: 24px;
  height: 24px;
  padding: 4px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 4px;
  margin-right: 5px;
  background-color: transparent;
  color: #131317;
  cursor: pointer;
}

.wlab-tag-picker__pin-btn:hover {
  background: rgba(19, 19, 23, 0.06);
}

:global(.dark) .wlab-tag-picker__pin-btn {
  color: #e0e0e0;
}

:global(.dark) .wlab-tag-picker__pin-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

.wlab-tag-picker__name {
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  width: 215px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wlab-tag-picker__empty {
  padding: 12px;
  text-align: center;
  color: #8d8d95;
  font-size: 13px;
}

.wlab-tag-picker__search {
  border-top: 1px solid #e2e2e4;
  display: flex;
  align-items: center;
  padding: 0 6px;
}

:global(.dark) .wlab-tag-picker__search {
  border-color: rgba(255, 255, 255, 0.1);
}

.wlab-tag-picker__bottom {
  border-top: 1px solid #e2e2e4;
  display: flex;
  align-items: center;
  padding: 0 6px;
  gap: 6px;
}

:global(.dark) .wlab-tag-picker__bottom {
  border-color: rgba(255, 255, 255, 0.1);
}

.wlab-tag-picker__bottom .wlab-tag-picker__search {
  border-top: none;
  padding: 0;
  flex: 1;
}

.wlab-tag-picker__search-input {
  height: 40px;
  border: none;
  border-radius: 0;
  flex: 2 1 0%;
  background: transparent;
  outline: none;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 22px;
  color: inherit;
  width: 100%;
  margin: 0 !important;

  &::placeholder {
    color: #8d8d95;
  }
}

.wlab-tag-picker__settings-btn {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: inherit;
  border-radius: 8px;
  cursor: pointer;
}

.wlab-tag-picker__settings-btn:hover {
  background: rgba(19, 19, 23, 0.06);
}

:global(.dark) .wlab-tag-picker__settings-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Dark theme: enforce true black tag picker palette */
:global(.dark) .wlab-tag-picker__item:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}

:global(.dark) .wlab-tag-picker__pin-btn {
  color: #ffffff !important;
}

:global(.dark) .wlab-tag-picker__pin-btn:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}

:global(.dark) .wlab-tag-picker__bottom {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

:global(.dark) .wlab-tag-picker__empty {
  color: rgba(255, 255, 255, 0.45) !important;
}

:global(.dark) .wlab-tag-picker__search-input::placeholder {
  color: rgba(255, 255, 255, 0.45) !important;
}

/* ── Canned Responses Picker (LiveChat-style) ── */
.wlab-canned-picker {
  position: fixed;
  background: #ffffff;
  border: none;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(19, 19, 23, 0.2);
  z-index: 8000;
  display: flex;
  flex-direction: column;
  color: #131317;
}

.wlab-canned-picker__list {
  display: block;
  border-radius: 4px 4px 0 0;
  min-width: 300px;
  max-height: 400px;
  overflow-y: auto;
}

.wlab-canned-picker__item {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  min-height: 20px;
  max-width: 100%;
  min-width: 260px;
  padding: 6px 10px;
  position: relative;
  border-bottom: 1px solid #e2e2e4;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f3f3f3;
  }
}

.wlab-canned-picker__item--group-first {
  padding-top: 39px;

  &::before {
    content: attr(data-group);
    display: flex;
    align-items: center;
    cursor: pointer;
    min-height: 20px;
    padding: 8px 10px 8px 20px;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    background-color: #f5f5f5;
    border-bottom: 1px solid #e2e2e4;
    color: #62626d;
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    box-sizing: border-box;
  }
}

.wlab-canned-picker__shortcode {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  word-break: break-word;
  width: 100%;
  padding: 0 10px;
}

.wlab-canned-picker__label {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 0;
}

.wlab-canned-picker__preview {
  font-size: 12px;
  line-height: 16px;
  color: #8d8d95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  padding: 0 10px;
}

.wlab-canned-picker__count {
  color: #62626d;
  width: 16px;
  padding-right: 5px;
  flex-shrink: 0;
}

.wlab-canned-picker__arrow {
  display: flex;
  align-items: center;
  padding-top: 1px;
  flex-shrink: 0;
}

.wlab-canned-picker__empty {
  padding: 16px;
  text-align: center;
  color: #8d8d95;
  font-size: 14px;
}

.wlab-canned-picker__footer {
  display: flex;
  padding: 4px 12px;
  align-items: center;
  border-top: 1px solid #e2e2e4;
  justify-content: space-between;
  position: relative;
}

.wlab-canned-picker__new {
  display: flex;
  align-items: center;
  color: #0066ff;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
  background: transparent;
  border: none;
  padding: 2px 0;
  font-family: inherit;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

.wlab-canned-picker__settings-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.wlab-canned-picker__settings {
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  color: inherit;
  display: flex;
  align-items: center;
  border: none;
  background: transparent;

  &:hover {
    background: #f3f3f3;
  }
}

.wlab-canned-picker__detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 8px;
  border-bottom: 1px solid #e2e2e4;
  position: sticky;
  top: 0;
  background: inherit;
  z-index: 1;
}

.wlab-canned-picker__back {
  width: 28px;
  height: 28px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: #f3f3f3;
  }
}

.wlab-canned-picker__detail-title {
  font-size: 13px;
  font-weight: 700;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

/* ── Settings popover for canned picker ── */
.wlab-canned-settings-popover {
  position: absolute;
  right: 0;
  bottom: 34px;
  width: 320px;
  background: #ffffff;
  border: 1px solid #e2e2e4;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(19, 19, 23, 0.22);
  padding: 10px 12px;
  z-index: 8100;
}

.wlab-canned-settings-popover__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 2px;
}

.wlab-canned-settings-popover__label {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: inherit;
}

.wlab-canned-settings-popover__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #e2e2e4;
  margin-top: 6px;
}

.wlab-canned-settings-popover__manage {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e2e2e4;
  border-radius: 8px;
  background: #ffffff;
  color: #131317;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.wlab-canned-settings-popover__manage:hover {
  background: #f5f5f5;
}

.wlab-canned-settings-popover__gear {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.wlab-canned-settings-popover__gear:hover {
  background: #f5f5f5;
}
</style>

<style lang="scss">
/* ══════════════════════════════════════════════════════
   DARK THEME — Tag bar & Tag picker
   Non-scoped block so body.dark selectors always work.
   ══════════════════════════════════════════════════════ */

/* ── Add tag button: icon + text → white ── */
html.dark body:not(.theme-mix) .wlab-tag-bar__add-btn,
body.dark:not(.theme-mix) .wlab-tag-bar__add-btn {
  color: #ffffff !important;

  span {
    color: #ffffff !important;
  }

  svg {
    color: #ffffff !important;
  }

  svg path {
    stroke: #ffffff !important;
    fill: none !important;
  }

  svg path[fill='currentColor'] {
    fill: #ffffff !important;
  }
}

/* ── Tag bar tags ── */
html.dark body:not(.theme-mix) .wlab-tag-bar__tag,
body.dark:not(.theme-mix) .wlab-tag-bar__tag {
  background: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.85) !important;
}

html.dark body:not(.theme-mix) .wlab-tag-bar__tag--suggested,
body.dark:not(.theme-mix) .wlab-tag-bar__tag--suggested {
  border-color: rgba(147, 197, 253, 0.55) !important;
  background: linear-gradient(
    120deg,
    rgba(30, 58, 138, 0.46) 0%,
    rgba(8, 47, 73, 0.58) 100%
  ) !important;
  color: #dbeafe !important;
}

html.dark body:not(.theme-mix) .wlab-tag-bar__tag--suggested .wlab-tag-bar__suggest-pill,
body.dark:not(.theme-mix) .wlab-tag-bar__tag--suggested .wlab-tag-bar__suggest-pill {
  background: rgba(147, 197, 253, 0.22) !important;
  color: #bfdbfe !important;
}

html.dark body:not(.theme-mix) .wlab-tag-bar__tag-remove,
body.dark:not(.theme-mix) .wlab-tag-bar__tag-remove {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* ── Tag picker dropdown: full dark palette ── */
html.dark body:not(.theme-mix) .wlab-tag-picker,
body.dark:not(.theme-mix) .wlab-tag-picker {
  background: #1a1a1e !important;
  color: #ffffff !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

html.dark body:not(.theme-mix) .wlab-tag-picker__item,
body.dark:not(.theme-mix) .wlab-tag-picker__item {
  color: #ffffff !important;

  &:hover {
    background: rgba(255, 255, 255, 0.06) !important;
  }
}

html.dark body:not(.theme-mix) .wlab-tag-picker__pin-btn,
body.dark:not(.theme-mix) .wlab-tag-picker__pin-btn {
  color: #ffffff !important;

  &:hover {
    background: rgba(255, 255, 255, 0.08) !important;
  }

  svg,
  svg path,
  svg g {
    color: #ffffff !important;
    stroke: #ffffff !important;
  }
}

html.dark body:not(.theme-mix) .wlab-tag-picker__name,
body.dark:not(.theme-mix) .wlab-tag-picker__name {
  color: #ffffff !important;
}

html.dark body:not(.theme-mix) .wlab-tag-picker__empty,
body.dark:not(.theme-mix) .wlab-tag-picker__empty {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark body:not(.theme-mix) .wlab-tag-picker__bottom,
body.dark:not(.theme-mix) .wlab-tag-picker__bottom {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark body:not(.theme-mix) .wlab-tag-picker__search,
body.dark:not(.theme-mix) .wlab-tag-picker__search {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark body:not(.theme-mix) .wlab-tag-picker__search-input,
body.dark:not(.theme-mix) .wlab-tag-picker__search-input {
  color: #ffffff !important;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4) !important;
  }
}

html.dark body:not(.theme-mix) .wlab-tag-picker__settings-btn,
body.dark:not(.theme-mix) .wlab-tag-picker__settings-btn {
  color: rgba(255, 255, 255, 0.6) !important;

  &:hover {
    background: rgba(255, 255, 255, 0.08) !important;
    color: #ffffff !important;
  }

  svg,
  svg path {
    color: inherit !important;
    stroke: currentColor !important;
  }
}
</style>
